import {
  DestroyRef,
  Inject,
  Injectable,
  InjectionToken,
  Injector,
  inject,
} from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import { AccountService } from '@service-features/account.service';
import { environment } from 'environments/environment';
import { BehaviorSubject, filter } from 'rxjs';

/**
 * Session recording with Microsoft Clarity. The Clarity projects use strict
 * masking (every text and image is hidden, configured in the dashboard); only
 * the fixed copy of the app (titles, labels, table headers, buttons, menus)
 * is shown, through `data-clarity-unmask` (see ClarityUnmaskDirective), and
 * data such as names, plates, amounts and notes stays hidden. This service
 * adds what the dashboard cannot do: never record signed out or on public
 * pages, stop on sensitive dialogs, honor the user's opt-out and tag each
 * session with the current screen (route without ids) to filter by it.
 *
 * The project comes from `environment.clarityProjectId`; empty means nothing
 * is recorded (see .claude/coordination/clarity-enmascarado.md).
 */
export const CLARITY_PROJECT_ID = new InjectionToken<string>(
  'CLARITY_PROJECT_ID',
  { providedIn: 'root', factory: () => environment.clarityProjectId },
);

export const SESSION_RECORDING_OPT_OUT_KEY =
  'garagedigital.analytics.sessionRecordingOptOut';

/** Pages anyone with the link can open: never recorded. */
const PUBLIC_PATHS = ['/vehicle-passport/', '/capture/'];

const ID_SEGMENT =
  /^(?:[0-9a-f]{24}|\d+|[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})$/i;

/** `/orders/65f0...a1/edit?x=1` becomes `/orders/:id/edit`. */
export function toScreenName(url: string): string {
  const path = url.split(/[?#]/, 1)[0];

  return (
    path
      .split('/')
      .map((segment) => (ID_SEGMENT.test(segment) ? ':id' : segment))
      .join('/') || '/'
  );
}

type ClarityFn = ((...args: unknown[]) => void) & { q?: unknown[][] };
type ClarityWindow = Window & { clarity?: ClarityFn };

function readOptOut(): boolean {
  try {
    return localStorage.getItem(SESSION_RECORDING_OPT_OUT_KEY) === 'true';
  } catch {
    return false;
  }
}

@Injectable({ providedIn: 'root' })
export class SessionRecordingService {
  private isSignedIn = false;
  private companyId: string | null = null;
  private isPublicPage = false;
  private pauseHolds = 0;
  private isLoaded = false;
  private isRecording = false;
  private isStarted = false;
  private screen = '';

  private readonly _isEnabled$ = new BehaviorSubject<boolean>(!readOptOut());
  public readonly isEnabled$ = this._isEnabled$.asObservable();

  constructor(
    @Inject(CLARITY_PROJECT_ID) private readonly projectId: string,
    // Resolved in start() so dialogs that only pause don't pull in the session.
    private readonly injector: Injector,
  ) {}

  get isAvailable(): boolean {
    return !!this.projectId;
  }

  /** Follows the session and the route; call once from the app root. */
  start(): void {
    if (!this.isAvailable || this.isStarted) return;

    const router = this.injector.get(Router);

    this.isStarted = true;
    this.injector.get(AccountService).company$.subscribe((company) => {
      const companyId = company?._id ?? null;
      const switchedWhileRecording =
        this.isRecording && companyId !== this.companyId;

      this.isSignedIn = companyId !== null;
      this.companyId = companyId;
      this.sync();

      // sync() identifies on start; a switch mid-recording needs it here.
      if (switchedWhileRecording && this.isRecording) this.identify();
    });
    this.updatePage(router.url);
    router.events
      .pipe(
        filter(
          (event): event is NavigationEnd => event instanceof NavigationEnd,
        ),
      )
      .subscribe((event) => this.updatePage(event.urlAfterRedirects));
  }

  setEnabled(enabled: boolean): void {
    try {
      if (enabled) {
        localStorage.removeItem(SESSION_RECORDING_OPT_OUT_KEY);
      } else {
        localStorage.setItem(SESSION_RECORDING_OPT_OUT_KEY, 'true');
      }
    } catch {
      // The choice still applies to this tab.
    }

    this._isEnabled$.next(enabled);
    this.sync();
  }

  /**
   * Stops the recording until the returned release runs. Holds stack, so
   * nested sensitive dialogs keep it stopped until the last one closes.
   */
  holdPause(): () => void {
    let released = false;

    this.pauseHolds += 1;
    this.sync();

    return () => {
      if (released) return;

      released = true;
      this.pauseHolds -= 1;
      this.sync();
    };
  }

  private updatePage(url: string): void {
    const path = url.split(/[?#]/, 1)[0];

    this.isPublicPage = PUBLIC_PATHS.some((prefix) => path.startsWith(prefix));
    this.screen = toScreenName(url);
    this.sync();
    // sync() tags the screen when it starts; this covers navigating while
    // already recording.
    if (this.isRecording) this.tagScreen();
  }

  private sync(): void {
    if (!this.isAvailable) return;

    const shouldRecord =
      this.isSignedIn &&
      this._isEnabled$.value &&
      !this.isPublicPage &&
      this.pauseHolds === 0;

    if (shouldRecord === this.isRecording) return;

    this.isRecording = shouldRecord;

    if (!this.isLoaded) {
      // Loading only once recording is allowed keeps the login out of every
      // session.
      this.load();
    } else {
      this.call(shouldRecord ? 'start' : 'stop');
    }

    if (shouldRecord) {
      this.identify();
      this.tagScreen();
    }
  }

  private load(): void {
    const w = window as ClarityWindow;

    this.isLoaded = true;
    w.clarity =
      w.clarity ??
      (((...args: unknown[]) => {
        (w.clarity!.q = w.clarity!.q ?? []).push(args);
      }) as ClarityFn);
    // Covered by the privacy policy accepted at sign-up; no ad cookies.
    this.call('consentv2', {
      ad_Storage: 'denied',
      analytics_Storage: 'granted',
    });

    const script = document.createElement('script');
    script.async = true;
    script.src = `https://www.clarity.ms/tag/${this.projectId}`;
    document.head.appendChild(script);
  }

  private identify(): void {
    // Opaque company id: lets support find a workshop's sessions without
    // sending names, emails or tax ids.
    if (this.companyId) this.call('identify', this.companyId);
  }

  private tagScreen(): void {
    if (this.screen) this.call('set', 'screen', this.screen);
  }

  private call(...args: unknown[]): void {
    try {
      (window as ClarityWindow).clarity?.(...args);
    } catch {
      // Clarity failures must never break the app: they only cost a recording.
    }
  }
}

/** Stops the recording while the calling component lives (payments, camera). */
export function pauseSessionRecordingWhileAlive(): void {
  const release = inject(SessionRecordingService).holdPause();

  inject(DestroyRef).onDestroy(release);
}
